import { AxiosError } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { TOAST_NOTIFICATION } from 'utils/constants/dataTest';
import { logError } from 'utils/helpers';
import { PaginationDirection } from 'utils/types';
import { AxiosErrorResponse } from 'utils/types';
import { NotificationContext } from '../../../containers/Notification';
import { UserContext } from '../useUserContext/context';
import CONTENT from './content';
import StoreApi from './StoreApi';
import {
  ShippingSettings,
  TestHistory,
  TestHistoryAttributes,
  TestHistoryPagination,
} from './types';

// Shipping Data

const TEST_HISTORY_URI = `/v1/users`;
const SHIPPING_INFO_URI = `/v1/settings/shipping_address`;

export const useAccountSettings = () => {
  const { setNotificationData } = useContext(NotificationContext);
  const {
    userData: { userId, loggedIn, isMember },
  } = useContext(UserContext);

  const [testsHistory, setTestsHistory] = useState<TestHistory[] | undefined>(
    undefined,
  );
  const [testsHistoryPagination, setTestsHistoryPagination] =
    useState<TestHistoryPagination | null>(null);

  const [shippingAddress, setShippingAddress] =
    useState<ShippingSettings | null>(null);

  // Loading flags
  const [loadingShippingInfo, setLoadingShippingInfo] = useState(false);
  const [loadingTestsHistory, setLoadingTestsHistory] = useState(false);

  const [savingShippingInfo, setSavingShippingInfo] = useState(false);
  const [savedShippingInfo, setSavedShippingInfo] = useState(false);
  const [invalidAddress, setInvalidAddress] = useState(false);

  const getShippingAddress = async () => {
    try {
      setLoadingShippingInfo(true);
      const { data } = await StoreApi.get(SHIPPING_INFO_URI);
      setShippingAddress({
        ...data,
        address1: data.street1,
        address2: data.street2,
        first_name: data.first_name,
        last_name: data.last_name,
      });

      return data;
    } catch (err) {
      setNotificationData({
        message: CONTENT.notifications.fetchFailed.title,
        persist: false,
        details: CONTENT.notifications.fetchFailed.message,
        type: TOAST_NOTIFICATION.ERROR_NOTIFICATION,
        source: ANALYTICS.LABELS.ORDER_MODAL,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Fetch Failed',
          component: ANALYTICS.LABELS.ORDER_MODAL,
          isMember,
        },
      });
      logError((err as Error).message, {
        component: 'ShippingSettings',
        method: 'loadInitialState',
      });

      return Promise.reject(err);
    } finally {
      setLoadingShippingInfo(false);
    }
  };

  const updateShippingAddress = async (shipping_address: ShippingSettings) => {
    const payload = { shipping_address };
    await analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.ORDER_MODAL_SAVE.label,
        description: ANALYTICS.LABELS.ORDER_MODAL_SAVE.description,
        isMember,
      },
    });
    try {
      setSavingShippingInfo(true);
      const { data } = await StoreApi.post(SHIPPING_INFO_URI, payload);
      setShippingAddress({
        ...data,
        address1: data.street1,
        address2: data.street2,
        first_name: data.first_name,
        last_name: data.last_name,
      });
      setInvalidAddress(false);
      setSavedShippingInfo(true);
      setNotificationData({
        message: CONTENT.notifications.updateSuccess.message,
        persist: false,
        source: ANALYTICS.LABELS.SHIPPING_INFORMATION,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_SUCCESS,
        data: {
          label: CONTENT.notifications.updateSuccess.message,
          component: ANALYTICS.LABELS.SHIPPING_INFORMATION,
          isMember,
        },
      });
      return data;
    } catch (err) {
      const invalidAddress =
        (err as AxiosErrorResponse).response?.data.code === 422;
      const invalidAddressMsg =
        (err as AxiosError).response?.data?.message === 'Address is invalid';

      setInvalidAddress(invalidAddress && invalidAddressMsg);

      if (invalidAddress && invalidAddressMsg)
        throw new Error((err as AxiosError).response?.data?.message);

      setNotificationData({
        message: CONTENT.notifications.updateFailed.title,
        persist: false,
        details: CONTENT.notifications.updateFailed.message,
        type: TOAST_NOTIFICATION.ERROR_NOTIFICATION,
        source: ANALYTICS.LABELS.ORDER_MODAL,
      });
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: 'Update Failed',
          component: ANALYTICS.LABELS.ORDER_MODAL,
          isMember,
        },
      });
      logError((err as AxiosError).message, {
        component: 'ShippingSettings',
        method: 'updateShippingInformation',
      });
      return Promise.reject(err);
    } finally {
      setSavingShippingInfo(false);
    }
  };

  const getTestHistory = async (direction?: PaginationDirection) => {
    const perPageDefault = 4;
    try {
      setLoadingTestsHistory(true);
      const page =
        direction && testsHistoryPagination?.meta
          ? // @ts-ignore
            testsHistoryPagination.meta[`${direction}-page`]
          : 1;

      const targetEndpoint = `${TEST_HISTORY_URI}/${userId}/membership/order_history.json?page=${page}&per_page=${perPageDefault}`;

      const {
        data: { data, links, meta },
      } = await StoreApi.get(targetEndpoint as string);

      const hasTests = Boolean(data && data.length);
      // TODO: place this method in Axios interceptor
      const formattedObj = hasTests
        ? data.map((test: TestHistoryAttributes) => ({
            ...test.attributes,
          }))
        : [];
      setTestsHistory(formattedObj);
      setTestsHistoryPagination({ links, meta });

      return data;
    } catch (err: any) {
      // Mixpanel event traking
      await analytics.track({
        event: ANALYTICS.EVENTS.API_FAILURE,
        data: {
          label: ANALYTICS.LABELS.TEST_HISTORY,
          isMember,
        },
      });
      // Datadog event traking
      logError(err?.message, {
        component: 'Tests history',
        method: 'getTestHistory',
      });
      return Promise.reject(err);
    } finally {
      setLoadingTestsHistory(false);
    }
  };

  const resetState = () => {
    setSavedShippingInfo(false);
  };

  useEffect(() => {
    if (isMember && !shippingAddress) getShippingAddress();
  }, [loggedIn, isMember]);

  return {
    getShippingAddress,
    updateShippingAddress,
    loadingShippingInfo,
    savingShippingInfo,
    shippingAddress,
    invalidAddress,
    savedShippingInfo,
    getTestHistory,
    loadingTestsHistory,
    testsHistory,
    testsHistoryPagination,
    resetState,
  };
};

export default useAccountSettings;
